import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useLocation, useNavigate } from "react-router-dom";
import { Sparkles, ArrowRight, KeyRound, AlertCircle, ExternalLink, CheckCircle2, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { lookupDelivery } from "@/lib/adminApi";
import ThemeToggle from "@/components/site/ThemeToggle";

/**
 * DeliveryPortal — public, cinematic page where clients redeem their access
 * code (PGFX-001-XXXX) to receive their Google Drive link.
 */
export default function DeliveryPortal() {
    const navigate = useNavigate();
    const location = useLocation();
    const [code, setCode] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState("");
    const [project, setProject] = useState(null);

    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const c = params.get("code");
        if (c) setCode(c.toUpperCase());
    }, [location.search]);

    const formatInput = (raw) => {
        const cleaned = raw.toUpperCase().replace(/[^A-Z0-9-]/g, "");
        return cleaned.slice(0, 16);
    };

    const submit = async (ev) => {
        ev?.preventDefault?.();
        if (!code.trim()) return;
        setSubmitting(true);
        setError("");
        try {
            const res = await lookupDelivery(code);
            setProject(res);
            toast.success("Project unlocked", { description: res.project_name });
        } catch (err) {
            const detail = err?.response?.data?.detail || "We couldn’t find a project for that code.";
            setError(typeof detail === "string" ? detail : "Invalid or expired access code.");
            toast.error("Access denied", { description: "Double-check the code or contact Pranto." });
        } finally {
            setSubmitting(false);
        }
    };

    const reset = () => {
        setProject(null);
        setCode("");
        setError("");
        navigate("/delivery", { replace: true });
    };

    return (
        <div
            className="relative min-h-screen overflow-hidden"
            style={{ background: "#050B0C", color: "#F2FBFA" }}
            data-testid="delivery-portal"
        >
            {/* Cinematic atmospheric gradients */}
            <div
                aria-hidden
                className="pointer-events-none absolute inset-0"
                style={{
                    background:
                        "radial-gradient(1100px 600px at 18% 12%, rgba(47,227,208,0.10) 0%, rgba(47,227,208,0) 60%), radial-gradient(900px 500px at 82% 30%, rgba(246,199,137,0.13) 0%, rgba(246,199,137,0) 55%), radial-gradient(700px 480px at 50% 100%, rgba(246,199,137,0.06) 0%, rgba(246,199,137,0) 70%)",
                }}
            />
            <div className="pg-grid-overlay absolute inset-0 opacity-40" aria-hidden />
            <div className="pg-noise" aria-hidden />

            {/* Nav */}
            <header className="relative z-10">
                <div className="pg-container flex items-center justify-between py-5">
                    <a href="/" className="flex items-center gap-2" data-testid="delivery-home-link">
                        <svg width="26" height="26" viewBox="0 0 28 28" fill="none" aria-hidden>
                            <rect x="1.5" y="1.5" width="25" height="25" rx="7" stroke="#F2FBFA" strokeOpacity="0.4" />
                            <path d="M9 19V9h5.4c2 0 3.3 1.2 3.3 3.1S16.4 15.2 14.4 15.2H11" stroke="#F2FBFA" strokeWidth="1.6" strokeLinecap="round" />
                            <circle cx="21" cy="21" r="1.5" fill="#F6C789" />
                        </svg>
                        <span className="font-display text-base" style={{ color: "#F2FBFA" }}>
                            Pranto<span style={{ color: "#F6C789" }}>.</span>Gfx
                        </span>
                    </a>
                    <ThemeToggle />
                </div>
            </header>

            <main className="relative z-10 pg-container flex min-h-[calc(100vh-72px)] flex-col items-center justify-center py-12">
                <AnimatePresence mode="wait">
                    {project ? (
                        <SuccessCard key="success" project={project} onReset={reset} />
                    ) : (
                        <LookupForm
                            key="lookup"
                            code={code}
                            onCodeChange={(v) => setCode(formatInput(v))}
                            submitting={submitting}
                            error={error}
                            onSubmit={submit}
                        />
                    )}
                </AnimatePresence>
            </main>
        </div>
    );
}

const LookupForm = ({ code, onCodeChange, submitting, error, onSubmit }) => (
    <motion.div
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -16 }}
        transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
        className="w-full max-w-xl text-center"
    >
        <motion.span
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="inline-flex items-center gap-2"
        >
            <Sparkles className="h-3.5 w-3.5" style={{ color: "#F6C789" }} />
            <span className="font-mono text-[11px] uppercase tracking-[0.22em]" style={{ color: "rgba(242,251,250,0.7)" }}>
                Client Delivery Portal · Pranto.Gfx
            </span>
        </motion.span>

        <motion.h1
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.65, delay: 0.18 }}
            className="font-display mt-5 text-[clamp(2.2rem,5vw,3.6rem)] leading-[1.05]"
            style={{ color: "#F2FBFA" }}
            data-testid="delivery-headline"
        >
            Your project is ready,{" "}
            <span className="pg-text-gold">enter your access code</span>.
        </motion.h1>

        <motion.p
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.65, delay: 0.28 }}
            className="mx-auto mt-4 max-w-lg text-base sm:text-lg"
            style={{ color: "rgba(242,251,250,0.7)" }}
        >
            Use the secure code Pranto shared with you. It will unlock your project’s Google Drive
            with all final assets.
        </motion.p>

        <motion.form
            onSubmit={onSubmit}
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.65, delay: 0.4 }}
            className="mx-auto mt-10 w-full max-w-md"
            data-testid="delivery-form"
        >
            <div
                className="flex items-center gap-2 rounded-2xl border p-2"
                style={{
                    borderColor: "rgba(246,199,137,0.35)",
                    background: "rgba(8,15,16,0.5)",
                    backdropFilter: "blur(8px)",
                    WebkitBackdropFilter: "blur(8px)",
                    boxShadow: "0 30px 60px -20px rgba(0,0,0,0.55), 0 0 30px rgba(246,199,137,0.08)",
                }}
            >
                <span
                    className="inline-flex h-11 w-11 items-center justify-center rounded-xl"
                    style={{ background: "rgba(246,199,137,0.16)", color: "#F6C789" }}
                >
                    <KeyRound className="h-4 w-4" strokeWidth={1.6} />
                </span>
                <input
                    type="text"
                    value={code}
                    onChange={(e) => onCodeChange(e.target.value)}
                    placeholder="PGFX-001-XXXX"
                    autoComplete="off"
                    spellCheck="false"
                    data-testid="delivery-code-input"
                    className="flex-1 bg-transparent px-2 py-3 font-mono text-base tracking-[0.22em] outline-none"
                    style={{ color: "#F2FBFA" }}
                />
                <button
                    type="submit"
                    disabled={submitting || !code.trim()}
                    data-testid="delivery-submit"
                    className="inline-flex items-center justify-center gap-1.5 rounded-xl px-4 py-3 text-sm font-medium transition-colors disabled:cursor-not-allowed disabled:opacity-60"
                    style={{ background: "#F6C789", color: "#071112" }}
                >
                    {submitting ? "Unlocking…" : "Unlock"}
                    {!submitting && <ArrowRight className="h-4 w-4" strokeWidth={1.6} />}
                </button>
            </div>
            {error && (
                <p
                    className="mt-3 flex items-center justify-center gap-1.5 text-[12px]"
                    style={{ color: "#FFB1B1" }}
                    data-testid="delivery-error"
                >
                    <AlertCircle className="h-3 w-3" /> {error}
                </p>
            )}
        </motion.form>

        <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.65, delay: 0.5 }}
            className="mx-auto mt-8 flex items-center justify-center gap-2 text-[11px] font-mono uppercase tracking-[0.22em]"
            style={{ color: "rgba(242,251,250,0.55)" }}
        >
            <ShieldCheck className="h-3.5 w-3.5" />
            Secured handover · Codes expire after archival
        </motion.div>
    </motion.div>
);

const SuccessCard = ({ project, onReset }) => (
    <motion.div
        initial={{ opacity: 0, y: 20, scale: 0.98 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        exit={{ opacity: 0, y: -10, scale: 0.98 }}
        transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
        className="w-full max-w-xl"
        data-testid="delivery-success"
    >
        <div
            className="relative overflow-hidden rounded-3xl border p-7 sm:p-9"
            style={{
                borderColor: "rgba(246,199,137,0.35)",
                background:
                    "linear-gradient(180deg, rgba(246,199,137,0.12) 0%, rgba(8,15,16,0.85) 100%)",
                boxShadow: "0 60px 100px -30px rgba(0,0,0,0.7), 0 0 60px rgba(246,199,137,0.10)",
                backdropFilter: "blur(10px)",
                WebkitBackdropFilter: "blur(10px)",
            }}
        >
            <span
                aria-hidden
                className="absolute inset-x-7 top-0 h-px"
                style={{
                    background:
                        "linear-gradient(90deg, transparent, #F6C789, #FCEACB, #F6C789, transparent)",
                }}
            />
            <div className="flex items-center gap-3">
                <motion.span
                    initial={{ scale: 0.6, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    transition={{ duration: 0.5, delay: 0.15, ease: [0.16, 1, 0.3, 1] }}
                    className="inline-flex h-12 w-12 items-center justify-center rounded-full"
                    style={{ background: "#F6C789", color: "#071112", boxShadow: "0 0 30px rgba(246,199,137,0.45)" }}
                >
                    <CheckCircle2 className="h-6 w-6" strokeWidth={1.6} />
                </motion.span>
                <div>
                    <span className="font-mono text-[10px] uppercase tracking-[0.22em]" style={{ color: "rgba(246,199,137,0.85)" }}>
                        Access Granted · {project.code}
                    </span>
                    <h2 className="font-display mt-1 text-2xl sm:text-3xl leading-tight" style={{ color: "#F2FBFA" }}>
                        Welcome, <span className="pg-text-gold">{project.client_name}</span>.
                    </h2>
                </div>
            </div>

            <div className="mt-6 grid gap-3 sm:grid-cols-2">
                <DetailRow label="Project" value={project.project_name} />
                {project.business_name && <DetailRow label="Business" value={project.business_name} />}
                <DetailRow label="Type" value={project.project_type} />
                <DetailRow label="Status" value={project.status} />
            </div>

            <p
                className="mt-6 rounded-2xl border p-4 text-sm leading-relaxed"
                style={{
                    borderColor: "rgba(242,251,250,0.18)",
                    background: "rgba(8,15,16,0.45)",
                    color: "rgba(242,251,250,0.85)",
                }}
                data-testid="delivery-message"
            >
                {project.delivery_message}
            </p>

            <div className="mt-7 flex flex-wrap items-center gap-3">
                <a
                    href={project.drive_link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 rounded-full px-5 py-3 text-sm font-medium transition-colors"
                    style={{
                        background: "#F6C789",
                        color: "#071112",
                        boxShadow: "0 18px 40px rgba(246,199,137,0.25)",
                    }}
                    data-testid="delivery-open-drive"
                >
                    Open Google Drive
                    <ExternalLink className="h-4 w-4" strokeWidth={1.6} />
                </a>
                <button
                    type="button"
                    onClick={onReset}
                    className="inline-flex items-center gap-2 rounded-full border px-5 py-3 text-sm transition-colors"
                    style={{ borderColor: "rgba(242,251,250,0.2)", color: "#F2FBFA" }}
                    data-testid="delivery-reset"
                >
                    Enter another code
                </button>
            </div>
        </div>
        <p
            className="mt-6 text-center font-mono text-[10px] uppercase tracking-[0.22em]"
            style={{ color: "rgba(242,251,250,0.55)" }}
        >
            If you need help, message Pranto on WhatsApp or Instagram.
        </p>
    </motion.div>
);

const DetailRow = ({ label, value }) => (
    <div
        className="rounded-xl border p-3.5"
        style={{ borderColor: "rgba(242,251,250,0.14)", background: "rgba(8,15,16,0.4)" }}
    >
        <div className="font-mono text-[10px] uppercase tracking-[0.22em]" style={{ color: "rgba(242,251,250,0.55)" }}>{label}</div>
        <div className="mt-1 text-sm" style={{ color: "#F2FBFA" }}>{value || "—"}</div>
    </div>
);
